'use client';

import { createStore, type Mutate, type StoreApi } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { STEPS, type Step } from './steps';
import type { VerticalPack } from './types';

/**
 * Extern Labs — flow state, as a per-vertical STORE FACTORY.
 *
 * Finance ships a module-level `create()` singleton (`use-finance-discovery-store.ts`). The engine
 * cannot: two verticals would share one store, so a user who ran Finance and then Data & Analytics
 * in the same session would see Finance's answers bleed across. So each vertical gets its OWN store,
 * built by `getLabsStore(pack)` and cached per slug (module-level, so it stays a singleton *within*
 * a vertical — the persist key, the container's hydration gate and the "start over" semantics all
 * behave exactly as they do in finance today).
 *
 * The other reason for a factory: `setM2Answer` must re-derive the archetype on every commit, and
 * the scoring function is PACK-SUPPLIED (`pack.deriveArchetype`). A module-scope store cannot read
 * React context, so the pack is closed over at construction instead.
 *
 * Persisted to localStorage. As in finance, the store is created with `skipHydration: true` and the
 * container is the single place that hydrates it — scoping the persist key per Clerk user id so one
 * user's draft can never surface for another.
 */

// ---------------------------------------------------------------------------
// Domain types — the same shapes finance persists, with the finance nouns dropped.
// ---------------------------------------------------------------------------

/** M1 — swipe left (not me) / right (that's me) on the day-in-the-life cards. */
export type SwipeDirection = 'left' | 'right';
export type M1SwipeAnswers = Record<string, SwipeDirection>;

/** M2 — mindset slider positions (0–6) keyed by question id. */
export type M2SliderAnswers = Record<string, number>;

/** M3 — answers to the derived-metric questions, keyed by metric key. */
export type M3Answers = Record<string, string>;

/**
 * M3 — the learner's own diagnosis of the practice subject, in their words.
 *
 * 🔴 ONLY EVER WHAT THEY TYPED. `M3Activity` auto-fills its textarea with the pack's SAMPLE diagnosis
 * when a learner reveals with the box empty, which is a good teaching move and a terrible thing to
 * persist: the takeaway artifact on the public preview reflects this field back as "your read", and
 * handing someone our sample paragraph under their own name is the kind of small lie that makes the
 * whole artifact worthless. The screen writes here on CHANGE only, never from the auto-fill.
 *
 * Empty string is the honest default and the artifact omits the section entirely when it is empty.
 */
export type M3Diagnosis = string;

/**
 * M4 — the learner's single-select chip per group: group id → chip id.
 *
 * NOTE: finance's store also declares an `m4Analysis` slice (`grossMargin` / `revenueGrowth` /
 * `verdict`) with a `setM4Analysis` action. It is DEAD — no screen reads or writes it; the M4 read is
 * expressed entirely through the chip picks graded against the answer key. It is not carried into the
 * engine.
 */
export type M4Picks = Record<string, string>;

interface LabsState {
  /** Pack-local archetype id (`pack.archetypes` key), or null before M2 resolves it. */
  archetype: string | null;
  fullName: string;
  m1Answers: M1SwipeAnswers;
  /** Furthest resolved M1 card index (swipes AND skips) — for refresh-resume past skipped cards. */
  m1Cursor: number;
  m2Answers: M2SliderAnswers;
  m3Answers: M3Answers;
  /** The learner's own words on the practice subject. See {@link M3Diagnosis}. */
  m3Diagnosis: M3Diagnosis;
  m4Picks: M4Picks;
  /** The chosen M4 subject id (a key of `pack.m4.subjects`). */
  m4Subject: string | null;
  /**
   * Monotonic run counter, bumped by `reset()`. The flow orchestrator is keyed on it, so "Start over"
   * remounts the flow — re-arming its per-run analytics latches (entered / step_viewed /
   * module_completed) so a replay is fully re-instrumented instead of being silently suppressed by
   * the once-per-mount Set/ref guards. A remount signal, not draft progress.
   */
  runId: number;
  step: Step;
}

interface LabsActions {
  back: () => void;
  goTo: (step: Step) => void;
  next: () => void;
  reset: () => void;
  setArchetype: (archetype: string | null) => void;
  setFullName: (name: string) => void;
  setM1Answer: (cardId: string, direction: SwipeDirection) => void;
  setM1Cursor: (cursor: number) => void;
  setM2Answer: (questionId: string, value: number) => void;
  setM3Answer: (metricKey: string, answer: string) => void;
  setM3Diagnosis: (diagnosis: M3Diagnosis) => void;
  setM4Pick: (groupId: string, chipId: string) => void;
  setM4Subject: (subjectId: string | null) => void;
}

export type LabsStore = LabsState & LabsActions;

/**
 * The store handle. The `persist` mutator is part of the TYPE, not just the runtime: the container
 * drives hydration through `store.persist.setOptions()` / `.rehydrate()`, which a bare
 * `StoreApi<LabsStore>` does not expose.
 */
export type LabsStoreApi = Mutate<StoreApi<LabsStore>, [['zustand/persist', unknown]]>;

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * The ordered sequence `next()` / `back()` walk, and the set `migrate` validates a persisted step
 * against. Defaults to the full 18-step sprint everywhere; the public preview passes a SUBSET of the
 * same ids (see `app/try/[vertical]`).
 *
 * A subset, never a new vocabulary: the step ids ARE the analytics vocabulary (see `./steps.ts`), so a
 * preview that invented its own could not be compared against the sprint it is a cut of. Reusing them
 * means "how far did a cold reader get" and "how far did a trialer get" are the same question asked of
 * the same field.
 */
export type LabsStepSequence = readonly Step[];

const initLabsState = (): LabsState => ({
  archetype: null,
  fullName: '',
  m1Answers: {},
  m1Cursor: 0,
  m2Answers: {},
  m3Answers: {},
  m3Diagnosis: '',
  m4Picks: {},
  m4Subject: null,
  runId: 0,
  step: 'welcome',
});

/**
 * The persist key for a vertical, before the container scopes it by identity.
 *
 * `variant` separates two flows over the SAME vertical: the gated sprint and the public preview are
 * different runs of different lengths, and sharing one key would mean a reader who did the 5-minute
 * preview and later subscribed would resume the full sprint mid-way through a module they never saw.
 */
export function labsDraftStorageKey(pack: VerticalPack, variant = ''): string {
  return variant ? `labs-${pack.slug}-${variant}-v1` : `labs-${pack.slug}-v1`;
}

export interface LabsStoreOptions {
  /** The ordered steps this flow walks. Defaults to the full sprint. */
  steps?: LabsStepSequence;
  /** Distinguishes this flow's persisted draft and cached instance from the sprint's. */
  variant?: string;
}

function createLabsStore(pack: VerticalPack, { steps = STEPS, variant = '' }: LabsStoreOptions = {}): LabsStoreApi {
  const isKnownArchetype = (id: unknown): id is string => typeof id === 'string' && Object.hasOwn(pack.archetypes, id);
  const stepIndex = (step: Step): number => steps.indexOf(step);
  // A sequence is non-empty by construction, but `steps[0]` is still `Step | undefined` to the
  // compiler; 'welcome' is both sequences' real first step, so the fallback is never taken.
  const firstStep: Step = steps[0] ?? 'welcome';
  const initState = (): LabsState => ({ ...initLabsState(), step: firstStep });

  return createStore<LabsStore>()(
    persist(
      (set, get) => ({
        ...initState(),

        goTo: (step) => set({ step }),
        next: () => {
          const nextStep = steps[Math.min(stepIndex(get().step) + 1, steps.length - 1)];
          if (nextStep) {
            set({ step: nextStep });
          }
        },
        back: () => {
          const prevStep = steps[Math.max(stepIndex(get().step) - 1, 0)];
          if (prevStep) {
            set({ step: prevStep });
          }
        },
        // Bump runId so the flow (keyed on it) remounts on "Start over", re-arming the per-run
        // analytics latches so the replay is re-instrumented.
        reset: () => set((s) => ({ ...initState(), runId: s.runId + 1 })),

        setFullName: (fullName) => set({ fullName }),
        setM1Answer: (cardId, direction) => set((s) => ({ m1Answers: { ...s.m1Answers, [cardId]: direction } })),
        setM1Cursor: (m1Cursor) => set({ m1Cursor }),
        // Committing an M2 answer re-derives the archetype from every answer so far — the slider quiz
        // IS the branch point that sets the orientation. The scoring is pack-supplied.
        setM2Answer: (questionId, value) =>
          set((s) => {
            const m2Answers = { ...s.m2Answers, [questionId]: value };
            return { archetype: pack.deriveArchetype(m2Answers), m2Answers };
          }),
        setArchetype: (archetype) => set({ archetype }),
        setM3Answer: (metricKey, answer) => set((s) => ({ m3Answers: { ...s.m3Answers, [metricKey]: answer } })),
        setM3Diagnosis: (m3Diagnosis) => set({ m3Diagnosis }),
        setM4Subject: (m4Subject) => set({ m4Subject }),
        setM4Pick: (groupId, chipId) => set((s) => ({ m4Picks: { ...s.m4Picks, [groupId]: chipId } })),
      }),
      {
        name: labsDraftStorageKey(pack, variant),
        version: 1,
        storage: createJSONStorage(() => localStorage),
        // Never auto-hydrate: the container sets a per-Clerk-userId scoped key, clears stale in-memory
        // state, then rehydrates — so one user's persisted draft can never leak into another's session.
        skipHydration: true,
        // Defensive, not a schema migration (v1 is the first shipped version): a persisted `step` or
        // `archetype` that this pack no longer defines would render a screen with no content, so an
        // unknown step snaps back to 'welcome' and an unknown archetype is dropped (M2 re-derives it).
        migrate: (persisted) => {
          const state = persisted as Partial<LabsState> | undefined;
          if (!state) {
            return persisted as LabsState;
          }
          const archetype = isKnownArchetype(state.archetype) ? state.archetype : null;
          // Validated against THIS flow's sequence, not the full sprint: a preview draft carrying a
          // step the preview does not run (from a shared key, or a shortened sequence) would otherwise
          // restore a screen the reader can never advance past.
          const step = steps.includes(state.step as Step) ? (state.step as Step) : firstStep;
          return { ...state, archetype, step } as LabsState;
        },
      },
    ),
  );
}

/**
 * The store for a vertical. Cached per slug so it stays a singleton WITHIN a vertical (finance's
 * semantics) while staying isolated BETWEEN verticals.
 *
 * The cache key includes `variant`, so the gated sprint and the public preview of the same vertical
 * get separate store instances. Without that they would share one, and the preview's shorter sequence
 * would silently become the sprint's — `next()` closes over `steps` at construction, so whichever flow
 * built the store first would define the other's navigation.
 */
const STORES = new Map<string, LabsStoreApi>();

export function getLabsStore(pack: VerticalPack, options: LabsStoreOptions = {}): LabsStoreApi {
  const cacheKey = options.variant ? `${pack.slug}:${options.variant}` : pack.slug;
  const existing = STORES.get(cacheKey);
  if (existing) {
    return existing;
  }
  const store = createLabsStore(pack, options);
  STORES.set(cacheKey, store);
  return store;
}
